/**
 * Panneau « Notes de dev » : **patch notes**, **crédits** et **Debug**, rien
 * d'autre depuis la v0.55.3 (A115 : les documents de conception, le tableau de
 * bord et la page de télémétrie ne sont plus proposés aux joueurs). L'onglet
 * Debug montre le panneau (`src/ui/debug/panel.ts`) quand le mode debug est
 * actif (`npm run dev`, préprod, `?debug`, ou **code** saisi ici,
 * `src/ui/debugCode.ts`) ; sinon il demande le code.
 */
import PATCHNOTES_MD from '../../../docs/PATCHNOTES.md?raw';
import { renderMarkdown } from '../markdown';
import { renderDebugTab } from '../debug/panel';
import type { ScreenCtx } from '../types';
import { CHANNEL, debugEnabledByChannel, debugUnlockedByCode } from '../channel';
import { lockDebug, unlockDebug, verifyDebugCode } from '../debugCode';
import { overlayRoot } from '../columnScale';
import { icon } from '../icons';
import { registerEscape } from '../keyboard';
import { creditsHtml } from '../credits';

export const APP_VERSION = "0.56.0";

type DocId = 'patch' | 'credits' | 'debug';

const DOCS: { id: DocId; label: string; md?: string }[] = [
  { id: 'patch', label: 'Patch notes', md: PATCHNOTES_MD },
  // Crédits (phase 5, A78) : revoyables ici, sans animation.
  { id: 'credits', label: 'Crédits' },
  { id: 'debug', label: 'Debug' },
];

const rendered = new Map<DocId, string>();

function docHtml(id: DocId): string {
  const cached = rendered.get(id);
  if (cached) return cached;
  const md = DOCS.find((d) => d.id === id)?.md ?? '';
  const html = renderMarkdown(md);
  rendered.set(id, html);
  return html;
}

export function isDebugEnabled(): boolean {
  return debugEnabledByChannel();
}

/**
 * Onglet Debug sans mode debug : la demande de code (v0.55.3). Un code accepté
 * pose la préférence et **recharge la page** : les outils de debug du démarrage
 * (`window.__dod`, vitesse de combat) ne s'installent qu'au chargement.
 */
function renderDebugCodePrompt(body: HTMLElement): void {
  body.innerHTML = `
    <div class="dev-code" data-role="debug-code">
      <p>Le mode debug est réservé au développement. Saisissez le code développeur pour l'activer sur cet appareil.</p>
      <input type="password" class="f-input" data-role="debug-code-input" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="Code développeur" aria-label="Code développeur" aria-describedby="debug-code-error">
      <button type="button" class="f-btn f-btn--primary f-btn--block" data-action="debug-code-submit">Activer le debug</button>
      <p class="gate-error" id="debug-code-error" data-role="debug-code-error" role="alert" hidden>Code refusé.</p>
    </div>`;
  const input = body.querySelector<HTMLInputElement>('[data-role="debug-code-input"]');
  const submit = body.querySelector<HTMLButtonElement>('[data-action="debug-code-submit"]');
  const error = body.querySelector<HTMLElement>('[data-role="debug-code-error"]');
  if (!input || !submit || !error) return;
  const trySubmit = async (): Promise<void> => {
    submit.disabled = true;
    const ok = await verifyDebugCode(input.value);
    if (ok) {
      unlockDebug();
      window.location.reload();
      return;
    }
    error.hidden = false;
    submit.disabled = false;
    input.select();
  };
  submit.addEventListener('click', () => void trySubmit());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void trySubmit();
    }
  });
  input.addEventListener('input', () => {
    error.hidden = true;
  });
  input.focus({ preventScroll: true });
}

/** Ouvre le panneau Dev par-dessus l'écran courant. */
export function openDevNotes(ctx: ScreenCtx, initial: DocId = 'patch'): void {
  const overlay = document.createElement('div');
  // v0.16.0 (refonte F1, lot 4) : voile, grand panneau facetté à filet d'or,
  // onglets de pierre (`.f-tabs`), typographie F1 du markdown ; icônes au lieu
  // des glyphes « ✕ » et « ↗ ».
  overlay.className = 'f-scrim dev-overlay';
  overlay.innerHTML = `
    <div class="f-modal dev-modal" data-role="dev-notes" role="dialog" aria-modal="true" aria-labelledby="dev-title">
      <div class="dev-head">
        <h2 class="dev-title" id="dev-title">Notes de dev <span class="dev-version">v${APP_VERSION}${CHANNEL === 'preprod' ? ' <span class="f-badge" data-tone="danger">Préprod</span>' : ''}</span></h2>
        <button type="button" class="f-ibtn" data-action="close" aria-label="Fermer">${icon('close')}</button>
      </div>
      <nav class="f-tabs f-tabs--wrap dev-tabs" role="tablist" aria-label="Documents">
        ${DOCS.map((d) => `<button type="button" class="f-tab" role="tab" aria-selected="false" data-doc="${d.id}">${d.label}</button>`).join('')}
      </nav>
      <div class="dev-body md" data-role="body" role="tabpanel"></div>
    </div>`;
  overlayRoot().appendChild(overlay);

  const body = overlay.querySelector<HTMLElement>('[data-role="body"]');
  const tabs = Array.from(overlay.querySelectorAll<HTMLButtonElement>('[data-doc]'));
  let active: DocId = initial;

  let unregisterEscape = (): void => {};
  const close = (): void => {
    unregisterEscape();
    overlay.remove();
  };
  unregisterEscape = registerEscape(overlay, close);

  const render = (): void => {
    for (const t of tabs) t.setAttribute('aria-selected', String(t.dataset.doc === active));
    if (!body) return;
    if (active === 'debug') {
      if (!isDebugEnabled()) {
        renderDebugCodePrompt(body);
      } else {
        renderDebugTab(body, ctx, {
          version: APP_VERSION,
          debug: true,
          close,
          rerender: render,
        });
        // Débloqué par code : on peut le refermer d'ici (la page se recharge sans les outils).
        if (debugUnlockedByCode()) {
          const off = document.createElement('button');
          off.type = 'button';
          off.className = 'f-btn f-btn--ghost f-btn--sm f-btn--block';
          off.setAttribute('data-action', 'debug-lock');
          off.textContent = 'Désactiver le mode debug sur cet appareil';
          off.addEventListener('click', () => {
            lockDebug();
            window.location.reload();
          });
          body.prepend(off);
        }
      }
    } else if (active === 'credits') {
      body.innerHTML = creditsHtml();
    } else {
      body.innerHTML = docHtml(active);
    }
    body.scrollTop = 0;
  };

  for (const t of tabs) {
    t.addEventListener('click', () => {
      active = (t.dataset.doc as DocId) ?? 'patch';
      render();
    });
  }
  overlay.querySelector('[data-action="close"]')?.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  render();
}
